import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { BookOpen, ExternalLink } from "lucide-react";

/**
 * ManualsView — read-only list of manuals attached to a DPI / Scaffolding.
 * Each row renders a NATIVE `<a target=_blank>` link to the public-file
 * endpoint. Using a real anchor (rather than an async fetch + blob-URL +
 * synthetic click) preserves the user-gesture, so mobile Safari / Chrome
 * actually open the file in a new tab instead of silently blocking it.
 * The path itself contains a UUID4 which serves as an unguessable token.
 */
export default function ManualsView({ kind, manualIds }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ids = Array.isArray(manualIds) ? manualIds : [];
    if (ids.length === 0) {
      setItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/manuals?kind=${kind}`);
        const byId = Object.fromEntries(data.map((m) => [m.id, m]));
        const ordered = ids.map((id) => byId[id]).filter(Boolean);
        if (!cancelled) setItems(ordered);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, JSON.stringify(manualIds || [])]); // eslint-disable-line

  if (loading) {
    return <div className="text-xs text-neutral-500">Caricamento manuali…</div>;
  }
  if (!items.length) return null;

  return (
    <div className="space-y-1.5" data-testid={`manuals-view-${kind}`}>
      {items.map((m) => (
        <div
          key={m.id}
          className="flex items-center gap-3 px-3 py-2 rounded-lg bg-orange-50/60 border border-orange-200"
          data-testid={`attached-manual-${m.id}`}
        >
          <BookOpen className="w-4 h-4 text-[#ff5a00] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{m.title}</div>
            <div className="text-[10px] text-neutral-500 truncate">Manuale · {m.filename}</div>
          </div>
          <a
            href={`${API}/public/files/${m.path}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bru-btn bru-btn-secondary !px-3 !py-1 text-xs"
            data-testid={`attached-manual-download-${m.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Apri
          </a>
        </div>
      ))}
    </div>
  );
}
